import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, ExternalLink, Filter } from "lucide-react";
import { locationLogsApi } from "../../lib/api";

/* ---------------- constants + helpers ---------------- */

const EVENT_META = {
  clock_in: { label: "Clocked in", tone: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  clock_out: { label: "Clocked out", tone: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30" },
  en_route: { label: "En Route", tone: "bg-orange-500/10 text-orange-300 border-orange-500/30" },
  on_site: { label: "On Site", tone: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30" },
  start_work: { label: "Start Work", tone: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  complete: { label: "Complete", tone: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
};

const STATUS_META = {
  green: { label: "≤ 1 mi", pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", dot: "bg-emerald-400" },
  red: { label: "> 1 mi", pill: "bg-rose-500/15 text-rose-300 border-rose-500/40", dot: "bg-rose-400" },
  unknown: { label: "No GPS", pill: "bg-slate-500/15 text-slate-300 border-slate-500/40", dot: "bg-slate-400" },
  no_job_coords: { label: "No job coords", pill: "bg-slate-500/15 text-slate-300 border-slate-500/40", dot: "bg-slate-400" },
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/* ---------------- Section ---------------- */

export default function LocationLogSection({ techs = [], staffUsers = [], fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ green: 0, red: 0, unknown: 0, no_job_coords: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [techId, setTechId] = useState("");
  const [status, setStatus] = useState("");
  const [event, setEvent] = useState("");

  // Merged user picker: field techs first, then dispatch/admin (all seen roles).
  // De-dupe by lowercased email so an admin/dispatcher who is also linked to
  // a technician record shows up only once in the filter dropdown.
  const users = useMemo(() => {
    const byEmail = new Map();
    const remember = (u, role) => {
      if (!u?.id) return;
      const key = (u.email || u.id).toLowerCase();
      if (byEmail.has(key)) return; // first hit wins (techs pushed in first below)
      byEmail.set(key, { id: u.id, name: u.name || u.email || u.id, role: role || u.role });
    };
    (techs || []).forEach((t) => remember({ id: t.userId, email: t.email, name: t.name }, "technician"));
    (staffUsers || []).forEach((u) => remember(u));
    return Array.from(byEmail.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [techs, staffUsers]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (techId) params.techId = techId;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (status) params.status = status;
    if (event) params.event = event;
    Promise.all([
      locationLogsApi.list(params),
      locationLogsApi.summary({ from: fromDate, to: toDate }),
    ])
      .then(([list, sum]) => {
        if (cancelled) return;
        setRows(Array.isArray(list) ? list : []);
        setSummary(sum || {});
      })
      .catch(() => { if (!cancelled) { setRows([]); setSummary({}); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [techId, status, event, fromDate, toDate]);

  return (
    <div className="space-y-4" data-testid="location-log-section">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Within 1 mi" value={summary.green || 0} color="emerald" />
        <StatTile label="Outside 1 mi" value={summary.red || 0} color="rose" />
        <StatTile label="No GPS captured" value={(summary.unknown || 0) + (summary.no_job_coords || 0)} color="slate" />
        <StatTile label="Total events" value={summary.total || 0} color="blue" />
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="location-log-filters">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 pr-1.5 border-r border-slate-200 mr-1">
          <Filter className="h-3.5 w-3.5 text-blue-600" /> Filters
        </div>

        <label className="text-[11px] font-semibold text-slate-500">Tech</label>
        <select
          value={techId}
          onChange={(e) => setTechId(e.target.value)}
          data-testid="location-log-tech"
          className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
          <option value="">All staff</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</option>
          ))}
        </select>

        <label className="text-[11px] font-semibold text-slate-500 ml-2">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="location-log-status"
          className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
          <option value="">Any</option>
          <option value="green">Within 1 mi (green)</option>
          <option value="red">Outside 1 mi (red)</option>
          <option value="unknown">No GPS</option>
          <option value="no_job_coords">No job coords</option>
        </select>

        <label className="text-[11px] font-semibold text-slate-500 ml-2">Event</label>
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          data-testid="location-log-event"
          className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
          <option value="">Any</option>
          <option value="clock_in">Clock in</option>
          <option value="clock_out">Clock out</option>
          <option value="en_route">En Route</option>
          <option value="on_site">On Site</option>
          <option value="start_work">Start Work</option>
          <option value="complete">Complete</option>
        </select>
      </div>

      {/* Log rows */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="location-log-list">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading location events…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            No location events for this filter.
          </div>
        ) : (
          <table className="w-full text-[13px]" data-testid="location-log-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">When</th>
                <th className="text-left px-3 py-2 font-semibold">Staff</th>
                <th className="text-left px-3 py-2 font-semibold">Event</th>
                <th className="text-left px-3 py-2 font-semibold">Job / Customer</th>
                <th className="text-left px-3 py-2 font-semibold">Location</th>
                <th className="text-right px-3 py-2 font-semibold">Distance</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const evMeta = EVENT_META[r.event] || { label: r.event, tone: "bg-slate-500/10 text-slate-500 border-slate-300" };
                const st = STATUS_META[r.status] || STATUS_META.unknown;
                return (
                  <tr key={r.id} className="hover:bg-slate-50 transition-colors" data-testid={`location-log-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtWhen(r.at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-900 font-semibold">
                      {r.userName || r.userEmail}
                      {r.role ? <span className="text-slate-400 font-normal ml-1">· {r.role}</span> : null}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11.5px] font-semibold ${evMeta.tone}`}>
                        {evMeta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {r.jobId ? (
                        <div className="flex flex-col leading-tight">
                          <span className="font-semibold text-slate-900">{r.jobId}</span>
                          <span className="text-[11.5px] text-slate-500">{r.customerName || "—"}</span>
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.techCoordsUrl ? (
                        <a
                          href={r.techCoordsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`location-log-map-${r.id}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          <MapPin className="h-3.5 w-3.5" />
                          <span className="tabular-nums">{r.techCoordsLabel}</span>
                          <ExternalLink className="h-3 w-3 opacity-60" />
                        </a>
                      ) : (
                        <span className="text-slate-400 italic">location unavailable</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-slate-700">
                      {r.distanceMiles != null ? `${r.distanceMiles.toFixed(2)} mi` : "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11.5px] font-semibold ${st.pill}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, color }) {
  const map = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${map[color] || map.slate}`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-[26px] font-bold tabular-nums leading-tight">{value.toLocaleString()}</div>
    </div>
  );
}
